/**
 * Shared listbox keyboard navigation composable.
 *
 * Provides open/close lifecycle, highlight tracking with scroll-into-view,
 * and a W3C ARIA-compliant keyboard handler (Arrow keys, Home/End,
 * PageUp/PageDown, Enter/Space, Escape, Tab, Alt combos).
 *
 * Requires x-ref="listbox" on the <ul> and [role="option"] on items.
 *
 * Extension points (override after spreading):
 *   getItemCount()       — return the number of visible options
 *   selectHighlighted()  — commit the currently highlighted option
 *   openDropdown()       — customise what happens on open
 *   close()              — customise what happens on close
 *
 * Usage:
 *   import listboxNav from './listboxNav';
 *   return { ...listboxNav(), getItemCount() { ... }, ... };
 */
export default function listboxNav() {
  return {
    open: false,
    highlightedIndex: -1,

    initHighlightScroll() {
      this.$watch('highlightedIndex', () => {
        this.$nextTick(() => this.scrollToHighlighted());
      });
    },

    // Safari bfcache: when the page is restored via back/forward,
    // Alpine's x-show/x-bind:class bindings may not re-render.
    // Force re-evaluation by clearing and restoring reactive props.
    initPageShowResync() {
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;

        const { thumbnail, description } = this;
        this.thumbnail = '';
        this.description = '';

        this.$nextTick(() => {
          this.thumbnail = thumbnail;
          this.description = description;
          if (this._onPageShowResync) {
            this._onPageShowResync();
          }
        });
      });
    },

    scrollToHighlighted() {
      const listbox = this.$refs.listbox;
      if (!listbox || this.highlightedIndex < 0) return;

      const highlighted = listbox.querySelector(
        `[data-item-index="${this.highlightedIndex}"]`,
      );
      if (!highlighted) return;

      const itemTop = highlighted.offsetTop;
      const itemBottom = itemTop + highlighted.offsetHeight;

      if (itemBottom > listbox.scrollTop + listbox.clientHeight) {
        listbox.scrollTop = itemBottom - listbox.clientHeight;
      } else if (itemTop < listbox.scrollTop) {
        listbox.scrollTop = itemTop;
      }
    },

    openDropdown() {
      if (this.open) return;
      this.open = true;
      this.highlightedIndex = this.nextEnabledIndex(0, 1);
    },

    close() {
      this.open = false;
      this.highlightedIndex = -1;
    },

    getItemCount() {
      return 0;
    },

    isItemDisabled(/* index */) {
      return false;
    },

    nextEnabledIndex(start, direction) {
      const count = this.getItemCount();
      if (count === 0) return -1;

      const step = direction >= 0 ? 1 : -1;
      let index = Math.max(0, Math.min(start, count - 1));

      for (let i = 0; i < count; i++) {
        if (!this.isItemDisabled(index)) return index;
        index += step;
        if (index >= count || index < 0) break;
      }

      return -1;
    },

    selectHighlighted() {},

    handleKeydown(event) {
      const { key, altKey } = event;
      const count = this.getItemCount();

      if (altKey) {
        if (key === 'ArrowDown') {
          event.preventDefault();
          if (!this.open) this.openDropdown();
          return;
        }
        if (key === 'ArrowUp' && this.open) {
          event.preventDefault();
          this.close();
          return;
        }
      }

      switch (key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!this.open) {
            this.openDropdown();
          } else if (count > 0) {
            const next = this.nextEnabledIndex(
              Math.min(this.highlightedIndex + 1, count - 1),
              1,
            );
            if (next >= 0) this.highlightedIndex = next;
          }
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (!this.open) {
            this.openDropdown();
            const last = this.nextEnabledIndex(count - 1, -1);
            if (last >= 0) this.highlightedIndex = last;
          } else if (count > 0) {
            const prev = this.nextEnabledIndex(
              Math.max(this.highlightedIndex - 1, 0),
              -1,
            );
            if (prev >= 0) this.highlightedIndex = prev;
          }
          break;

        case 'Home':
          if (this.open && count > 0) {
            event.preventDefault();
            const first = this.nextEnabledIndex(0, 1);
            if (first >= 0) this.highlightedIndex = first;
          }
          break;

        case 'End':
          if (this.open && count > 0) {
            event.preventDefault();
            const last = this.nextEnabledIndex(count - 1, -1);
            if (last >= 0) this.highlightedIndex = last;
          }
          break;

        case 'Enter':
        case ' ':
          if (this.open) {
            event.preventDefault();
            if (
              this.highlightedIndex >= 0 &&
              !this.isItemDisabled(this.highlightedIndex)
            ) {
              this.selectHighlighted();
            }
          } else {
            event.preventDefault();
            this.openDropdown();
          }
          break;

        case 'Escape':
          if (this.open) {
            event.preventDefault();
            this.close();
          }
          break;

        case 'Tab':
          if (this.open) {
            this.close();
          }
          break;

        case 'PageUp':
          if (this.open && count > 0) {
            event.preventDefault();
            const target = this.nextEnabledIndex(
              Math.max(this.highlightedIndex - 10, 0),
              -1,
            );
            if (target >= 0) this.highlightedIndex = target;
          }
          break;

        case 'PageDown':
          if (this.open && count > 0) {
            event.preventDefault();
            const target = this.nextEnabledIndex(
              Math.min(this.highlightedIndex + 10, count - 1),
              1,
            );
            if (target >= 0) this.highlightedIndex = target;
          }
          break;
      }
    },
  };
}
